import type {PluginName} from '@packages/systems/wfdl-Qualified';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {
  RelationshipConstraint,
  PageConstraint,
  NormalizedConstraints,
} from '@packages/systems/constraints-types';
import {getName} from '@packages/systems/wfdl-Qualified';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {Component} from '@packages/systems/wfdl';

export const hasParentOrWrapsRelationship = (
  componentMap: Component.ComponentMap,
  element: ExpressionElement,
  constraints: NormalizedConstraints
) =>
  Object.keys(constraints).some((relationshipType) => {
    switch (relationshipType) {
      case 'wraps':
        return Boolean(
          constraints[relationshipType].includes(getName(element.type))
        );
      case 'children': {
        return hasMatchingConstraintRelationship(
          componentMap,
          element,
          constraints,
          relationshipType
        );
      }
    }

    return false;
  });

export const hasChildrenRelationship = (
  componentMap: Component.ComponentMap,
  element: ExpressionElement,
  constraints: NormalizedConstraints
) =>
  Object.keys(constraints).some((relationshipType) => {
    switch (relationshipType) {
      case 'parent': {
        return hasMatchingConstraintRelationship(
          componentMap,
          element,
          constraints,
          relationshipType
        );
      }
    }

    return false;
  });

export const hasAncestorsOrDescendantsRelationship = (
  componentMap: Component.ComponentMap,
  element: ExpressionElement,
  constraints: NormalizedConstraints,
  relationshipType: 'ancestors' | 'descendants'
) =>
  hasMatchingConstraintRelationship(
    componentMap,
    element,
    constraints,
    relationshipType
  );

const hasMatchingConstraintRelationship = (
  componentMap: Component.ComponentMap,
  element: ExpressionElement,
  constraints: NormalizedConstraints,
  relationshipType: 'ancestors' | 'descendants' | 'parent' | 'children'
) => {
  for (const constraint of constraints[relationshipType]) {
    switch (constraint.rule) {
      case 'AtLeastOne':
      case 'ExactlyOne':
      case 'ZeroOrOne':
      case 'AtMostN':
      case 'RequireOnly': {
        return relationshipConstraintMatches({
          constraint,
          element,
          componentMap,
          bindingContextMatchesExternalKey: null,
        });
      }
    }
  }

  return false;
};

export function relationshipConstraintMatches({
  constraint,
  element,
  componentMap,
  bindingContextMatchesExternalKey,
}: {
  constraint: RelationshipConstraint;
  element: ExpressionElement;
  componentMap: Component.ComponentMap;
  bindingContextMatchesExternalKey:
    | ((arg1: PluginName, arg2: string) => boolean)
    | null;
}): boolean {
  // Check if the constraint check is an element type.
  if (typeof constraint.is === 'string') {
    return constraint.is === getName(element.type);
  }
  // Check if the constraint check is a union of element types.
  else if (Array.isArray(constraint.is)) {
    return constraint.is.includes(getName(element.type));
  }
  // Check the constraint check using the escape hatch.
  else {
    return constraint.is.check(
      element,
      componentMap,
      bindingContextMatchesExternalKey
    );
  }
}

export function pageConstraintMatches(
  constraint: PageConstraint,
  page: PageRecordType
) {
  // Check if the constraint check is a page utilKey type.
  if (typeof constraint.is === 'string') {
    return constraint.is === page.utilKey;
  }
  // Check if the constraint check is a union of utilKey types.
  else if (Array.isArray(constraint.is)) {
    return Boolean(page.utilKey && constraint.is.includes(page.utilKey));
  }
  // Check the constraint check using the page escape hatch.
  else {
    return constraint.is.check(page);
  }
}
